/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: modules/wireguard-client/hooks/useWireGuardClient.ts
 🗓️ Created: 2026-05-13
 🧩 Description:
    Hook for the WireGuard Client module (Ring2All PBX).
    Mirrors useOpenVPNClient.ts pattern exactly.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../hooks/useNotifications';
import { useI18n } from '../../../hooks/useI18n';
import * as api from '../api/wireguardClientApi';
import type { WireGuardClientStatus } from '../types';

export function useWireGuardClient() {
    const user       = useAuthStore((state) => state.user);
    const tenantSlug = user?.tenant?.slug;
    const { showSuccess, showError } = useNotifications();
    const { t } = useI18n();

    const [status, setStatus]         = useState<WireGuardClientStatus | null>(null);
    const [loading, setLoading]       = useState(true);
    const [uploading, setUploading]   = useState(false);
    const [connecting, setConnecting] = useState(false);

    // Load status
    const loadStatus = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.getWireGuardClientStatus(tenantSlug, user?.id);
            if (response.success) setStatus(response.data);
        } catch (error) {
            console.error('Failed to load WireGuard client status:', error);
        } finally {
            setLoading(false);
        }
    }, [tenantSlug, user?.id]);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    // Upload config — automatically reconnects if the tunnel was active
    const uploadConfig = useCallback(async (wgConfContent: string) => {
        setUploading(true);
        setConnecting(true);   // block Connect/Disconnect buttons during import + possible reconnect
        try {
            const response = await api.uploadWireGuardConfig(wgConfContent, tenantSlug, user?.id);
            if (response.success) {
                setStatus(response.data);
                const msg = response.data.connected
                    ? t('wireguardClient.messages.uploadReconnected', 'Configuration imported and VPN reconnected automatically')
                    : t('wireguardClient.messages.uploadSuccess', 'WireGuard configuration imported. Press Connect to establish the tunnel.');
                showSuccess(msg);
                return true;
            }
            return false;
        } catch (error: any) {
            console.error('Failed to upload WireGuard config:', error);
            showError(error.message || t('wireguardClient.messages.uploadError', 'Failed to import WireGuard configuration'));
            return false;
        } finally {
            setUploading(false);
            setConnecting(false);
        }
    }, [tenantSlug, user?.id, showSuccess, showError, t]);

    // Connect
    const connect = useCallback(async () => {
        setConnecting(true);
        try {
            const response = await api.connectWireGuard(tenantSlug, user?.id);
            if (response.success) {
                setStatus(response.data);
                showSuccess(t('wireguardClient.messages.connectSuccess', 'WireGuard VPN connected successfully'));
                return true;
            }
            return false;
        } catch (error: any) {
            console.error('Failed to connect WireGuard:', error);
            showError(error.message || t('wireguardClient.messages.connectError', 'Failed to connect to WireGuard VPN'));
            return false;
        } finally {
            setConnecting(false);
        }
    }, [tenantSlug, user?.id, showSuccess, showError, t]);

    // Disconnect
    const disconnect = useCallback(async () => {
        setConnecting(true);
        try {
            const response = await api.disconnectWireGuard(tenantSlug, user?.id);
            if (response.success) {
                setStatus(response.data);
                showSuccess(t('wireguardClient.messages.disconnectSuccess', 'WireGuard VPN disconnected'));
                return true;
            }
            return false;
        } catch (error: any) {
            console.error('Failed to disconnect WireGuard:', error);
            showError(error.message || t('wireguardClient.messages.disconnectError', 'Failed to disconnect WireGuard VPN'));
            return false;
        } finally {
            setConnecting(false);
        }
    }, [tenantSlug, user?.id, showSuccess, showError, t]);

    // Delete configuration
    const deleteConfiguration = useCallback(async () => {
        try {
            const response = await api.deleteWireGuardConfig(tenantSlug, user?.id);
            if (response.success) {
                await loadStatus();
                showSuccess(t('wireguardClient.messages.deleteSuccess', 'WireGuard configuration deleted'));
                return true;
            }
            return false;
        } catch (error: any) {
            console.error('Failed to delete WireGuard config:', error);
            showError(error.message || t('wireguardClient.messages.deleteError', 'Failed to delete WireGuard configuration'));
            return false;
        }
    }, [tenantSlug, user?.id, loadStatus, showSuccess, showError, t]);

    // Refresh status
    const refreshStatus = useCallback(async () => {
        try {
            const response = await api.refreshWireGuardStatus(tenantSlug, user?.id);
            if (response.success) setStatus(response.data);
        } catch (error) {
            console.error('Failed to refresh WireGuard status:', error);
        }
    }, [tenantSlug, user?.id]);

    return {
        status,
        loading,
        uploading,
        connecting,
        uploadConfig,
        connect,
        disconnect,
        deleteConfiguration,
        refreshStatus,
        reload: loadStatus,
    };
}
